import { useEffect, useState } from "react";
import { Modal, Button, Space, Divider, Typography } from "antd";
import { DownloadOutlined, UndoOutlined } from "@ant-design/icons";
import { PDFViewer, PDFDownloadLink } from "@react-pdf/renderer";
import { kebabCase } from "change-case";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import Certificate from "./certificate-document";
import CertificateEditablePreview from "./certificate-editable-preview";

const { Text } = Typography;

const CertificatePreviewModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isModalOpen, modalType, modalProps } = useSelector((state) => state.modals);

  const open = isModalOpen && modalType === "certificate_preview";
  const baseData = modalProps?.certificateData;

  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    if (!open) {
      setOverrides({});
    }
  }, [open]);

  const onClose = () => dispatch(closeModal());

  if (!baseData) {
    return null;
  }

  const certificateData = { ...baseData, overrides };
  const hasEdits = Object.values(overrides).some((v) => v?.trim());
  const fileName = `${kebabCase(baseData?.student?.fullName || "person")}-${
    baseData?.type === "training" ? "formation" : baseData?.type === "vacation" ? "vacation" : "stage"
  }-certificate.pdf`;

  return (
    <Modal
      title={t("certificates.preview.modalTitle", "Aperçu de l'attestation")}
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnClose
      centered
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        {t(
          "certificates.preview.editHint",
          "Survolez un champ et cliquez sur l'icône crayon pour le modifier directement sur l'attestation."
        )}
      </Text>

      <Space style={{ marginBottom: 16 }} wrap>
        {hasEdits && (
          <Button icon={<UndoOutlined />} onClick={() => setOverrides({})}>
            {t("certificates.preview.reset", "Réinitialiser le texte")}
          </Button>
        )}
        <PDFDownloadLink fileName={fileName} document={<Certificate data={certificateData} />}>
          {({ loading }) => (
            <Button type="primary" icon={<DownloadOutlined />} loading={loading}>
              {t("certificates.download", "Télécharger le Certificat")}
            </Button>
          )}
        </PDFDownloadLink>
      </Space>

      <CertificateEditablePreview data={baseData} overrides={overrides} onChange={setOverrides} />

      <Divider>{t("certificates.preview.finalPdf", "Aperçu final du PDF")}</Divider>

      <PDFViewer
        width="100%"
        height={360}
        showToolbar={false}
        style={{ border: "1px solid #f0f0f0", borderRadius: 8 }}
      >
        <Certificate data={certificateData} />
      </PDFViewer>
    </Modal>
  );
};

export default CertificatePreviewModal;
